import type { AnyVariables, CombinedError, OperationResult } from "@urql/core";
import { useOnline } from "@vueuse/core";
import { useToast } from "primevue/usetoast";

export function useMutationNotification() {
  const toaster = useToast();
  const online = useOnline();

  // set textOveride true to use any string passed into operationName
  // as the toast message. Combining the operationName with 'Successful'
  // doen't always make sense grammatically.
  function handleToastResponse(
    operationName: string,
    result: { data?: unknown; error?: CombinedError },
    textOveride?: boolean
  ) {
    const { data, error } = result;
    if (data) {
      toaster.add({
        severity: "success",
        summary: "Success",
        life: 10000,
        detail: textOveride ? operationName : `${operationName} Successful`
      });
    } else if (error && error?.message !== "[Network] Failed to fetch") {
      toaster.add({
        severity: "error",
        summary: "Error",
        detail: `${operationName} Failed. ${error.message}`
      });
    }
  }

  function registerResultToast(
    operationName: string,
    result: Promise<OperationResult<unknown, AnyVariables>>
  ) {
    // when offline just say it worked ¯\_(ツ)_/¯
    if (!online.value) {
      toaster.add({
        severity: "success",
        summary: "Offline Success",
        life: 10000,
        detail: `${operationName} Saved Locally.  Operation will submit when you return online.`
      });
    }

    // fires when back online and operation resolves
    result.then(result => {
      handleToastResponse(operationName, result);
    });
  }
  return { registerResultToast, handleToastResponse };
}
